// Global Instructions Rule Applied!
//
// How a statement run reads on its form: the quarters a run can be made for,
// what a fund's state is called, file sizes, and the header tags.

import dayjs, { type Dayjs } from 'dayjs'
import {
  InvestorStatementRun,
  InvestorStatementRunFund,
  InvestorStatementRunFundGroup,
  RunFundState
} from '../../../state_manager/data_models/investor_statement_run.ts'

export const DATE_FORMAT = 'MM/DD/YYYY'
export const ISO_DATE = 'YYYY-MM-DD'

/** How tall a section's table grows on the run form before its rows scroll
 *  under a fixed header: a run of many funds would otherwise push the
 *  sections below it several screens down. */
export const RUN_TABLE_SCROLL = { y: 440 }

/** The last day of the quarter a date falls in. */
export const quarterEnd = (d: Dayjs): Dayjs =>
  d
    .month(Math.floor(d.month() / 3) * 3 + 2)
    .endOf('month')
    .startOf('day')

/** The last quarter that has ended: statements are made for a finished quarter. */
export const lastCompletedQuarterEnd = (today: Dayjs = dayjs()): Dayjs =>
  quarterEnd(today.startOf('month').subtract((today.month() % 3) + 1, 'month'))

/** '2026 Q2' for a period end. */
export const periodLabel = (periodEnd: string | Dayjs): string => {
  const d = dayjs(periodEnd)
  return `${d.year()} Q${Math.floor(d.month() / 3) + 1}`
}

/**
 * The period ends a run can be made for: the quarters that have ended, as
 * select options - '2026 Q2' valued '2026-06-30' - newest first (the field
 * asks the select to keep that order). `include` keeps a run's own period on
 * the list when it is older than the list reaches.
 */
export function periodOptions(
  today: Dayjs = dayjs(),
  count = 12,
  include?: string | null
): Array<{ value: string; label: string }> {
  const ends: string[] = []
  let end = lastCompletedQuarterEnd(today)
  for (let i = 0; i < count; i += 1) {
    ends.push(end.format(ISO_DATE))
    end = quarterEnd(end.startOf('month').subtract(3, 'month'))
  }
  if (include && !ends.includes(include)) ends.push(include)
  return ends
    .sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
    .map((value) => ({ value, label: periodLabel(value) }))
}

/** 'no_legal_name' -> 'No legal name' */
export const checkLabel = (code: string): string => {
  const words = code.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** '2.7 MB', '85 KB'; '—' for nothing stored. */
export function fileSizeText(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—'
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** What the state Tag and the Issued cell read from: a fund of a run, on the
 *  run form or in the list of a fund's runs. */
type RunFundStanding = Pick<
  InvestorStatementRunFund,
  'prog__state' | 'prog__counts' | 'prog__issued_count' | 'prog__issued_at' | 'prog__revised_unissued_count'
>

/** Every current statement of the fund is marked as issued. */
export const isIssued = (fund: RunFundStanding): boolean =>
  fund.prog__counts.total > 0 && fund.prog__issued_count === fund.prog__counts.total

/** The Issued cell of a fund: '10/05/2026', '29 of 30 · 1 revised to issue',
 *  '—' when nothing of it is issued. */
export function issuedText(fund: RunFundStanding): string {
  const revised = fund.prog__revised_unissued_count
  const waiting = revised > 0 ? `${revised} revised to issue` : ''
  if (fund.prog__issued_count === 0) return waiting || '—'
  const when = fund.prog__issued_at ? dayjs(fund.prog__issued_at).format(DATE_FORMAT) : 'Issued'
  if (isIssued(fund)) return when
  return [`${fund.prog__issued_count} of ${fund.prog__counts.total} · ${when}`, waiting].filter(Boolean).join(' · ')
}

/** What differs, for the Out of date tooltip: the parts of a stored
 *  statement's snapshot that no longer match the live read (a fund report:
 *  the Period Highlights set it carries, or the report the run prints). */
export function changedPartsText(parts: string[]): string {
  const said = new Set<string>()
  parts.forEach((part) => {
    if (part === 'settings') said.add('the statement settings or wording')
    else if (part === 'entity') said.add("the fund's details")
    else if (part === 'highlights') said.add('the Period Highlights set (a new version or its sign-off)')
    else if (part === 'report_code') said.add('the report the run prints')
    else said.add('the books')
  })
  const list = Array.from(said)
  if (list.length === 0) return 'The investor is no longer on the statements of this period.'
  return `Changed since it was generated: ${list.join(', ')}.`
}

/** The document groups a fund of a run produces documents of (the reports
 *  group only while Include Fund Report is on), in display order. */
export const enabledGroups = (fund: Pick<InvestorStatementRunFund, 'prog__groups'>): InvestorStatementRunFundGroup[] =>
  fund.prog__groups.filter((g) => g.prog__enabled)

/** Every current document of the group is marked as issued. */
export const isGroupIssued = (group: InvestorStatementRunFundGroup): boolean =>
  group.prog__counts.total > 0 && group.prog__issued_count === group.prog__counts.total

/** Every current document of the group is generated: it can be marked as
 *  issued (the server's rule: stmt__run_fund_issue). */
export const isGroupIssuable = (group: InvestorStatementRunFundGroup): boolean =>
  group.prog__counts.total > 0 && group.prog__counts.generated === group.prog__counts.total

/** Where a fund stands in one document group, for the Status column:
 *  'Issued 10/29/2026', 'Generated 12 of 14', '2 failed', 'Generating',
 *  '1 revised to issue', '—' for nothing yet. */
export function groupStateText(group: InvestorStatementRunFundGroup): string {
  const counts = group.prog__counts
  if (counts.total === 0) return '—'
  if (counts.pending > 0) return `Generating ${counts.total - counts.pending} of ${counts.total}`
  if (counts.failed > 0) return `${counts.failed} failed`
  const revised = group.prog__revised_unissued_count
  if (isGroupIssued(group)) {
    return group.prog__issued_at ? `Issued ${dayjs(group.prog__issued_at).format(DATE_FORMAT)}` : 'Issued'
  }
  if (group.prog__issued_count > 0) {
    return [`${group.prog__issued_count} of ${counts.total} issued`, revised > 0 ? `${revised} revised to issue` : '']
      .filter(Boolean)
      .join(' · ')
  }
  return counts.generated === counts.total ? `Generated ${counts.total}` : `Generated ${counts.generated} of ${counts.total}`
}

/** The Tag of a fund's state on the run form (the house status vocabulary). */
export function runFundStateTag(fund: RunFundStanding): { color?: string; text: string; tooltip?: string } {
  const counts = fund.prog__counts
  switch (fund.prog__state) {
    case RunFundState.queued:
      return { color: 'blue', text: 'Queued' }
    case RunFundState.generating:
      return {
        color: 'blue',
        text:
          counts.total > 0 ? `Generating ${counts.total - counts.pending} of ${counts.total}` : 'Generating'
      }
    case RunFundState.stalled:
      return {
        color: 'gold',
        text: 'Stalled',
        tooltip: 'Generation did not finish: the server stopped while these statements were being produced. Generate them again.'
      }
    case RunFundState.blocked:
      return { color: 'red', text: 'Blocked' }
    case RunFundState.failed:
      return { color: 'red', text: counts.failed > 0 ? `${counts.failed} failed` : 'Failed' }
    case RunFundState.generated:
      return { color: 'green', text: 'Generated' }
    default:
      return { text: 'Not generated' }
  }
}

/** The header Tags of the Statements section: what the stored statements of
 *  the run add up to. */
export function runSummaryTags(run: InvestorStatementRun): Array<{ key: string; color?: string; text: string }> {
  const s = run.prog__summary
  const tags: Array<{ key: string; color?: string; text: string }> = []
  if (s.statements === 0) return tags
  if (s.generated > 0) tags.push({ key: 'generated', color: 'green', text: `${s.generated} of ${s.statements} generated` })
  if (s.pending > 0) tags.push({ key: 'pending', color: 'blue', text: `${s.pending} generating` })
  if (s.failed > 0) tags.push({ key: 'failed', color: 'red', text: `${s.failed} failed` })
  if (s.stalled > 0) tags.push({ key: 'stalled', color: 'gold', text: `${s.stalled} stalled` })
  if (s.file_size_bytes > 0) tags.push({ key: 'size', text: fileSizeText(s.file_size_bytes) })
  return tags
}
